// The spectator crowd: instanced cats in varied coats sitting in the grandstand rows.
// Each moves on its own in the vertex shader: breathing, heads following the pack, tail swishes,
// and, when the race comes past or reaches the finish, paws in the air and little hops.
// The crowd fills a window of grandstand modules around the action; crowd cards do the rest.
import {
  BufferAttribute, BufferGeometry, Color, ConeGeometry, CylinderGeometry, Group, InstancedBufferAttribute,
  InstancedMesh, Matrix4, MeshStandardMaterial, Quaternion, SphereGeometry, Vector3,
} from 'three';
import type { Phase } from '../../race/types';
import {
  OCCUPANCY, SEATS_PER_ROW, SEAT_SPACING, STAND, crowdModules, crowdWindowStart, rng, rowSeat,
} from './layout';
import { merge, normalise, place } from './util';

/** Body parts, used by the vertex shader to animate each separately. */
const PART = { body: 0, head: 1, eye: 2, armL: 3, armR: 4, tail: 5 } as const;

/** Builds one sitting spectator cat (about 0.55 m tall), facing +z, origin on the seat. */
function spectatorGeometry(): BufferGeometry {
  const parts: BufferGeometry[] = [];
  const tag = (g: BufferGeometry, part: number, pivot: [number, number, number], white: (p: Vector3) => number) => {
    const n = normalise(g);
    const pos = n.getAttribute('position');
    const a = new Float32Array(pos.count * 4);
    const pv = new Float32Array(pos.count * 3);
    const v = new Vector3();
    for (let i = 0; i < pos.count; i++) {
      v.fromBufferAttribute(pos, i);
      a[i * 4] = part; a[i * 4 + 1] = white(v);
      pv[i * 3] = pivot[0]; pv[i * 3 + 1] = pivot[1]; pv[i * 3 + 2] = pivot[2];
    }
    n.setAttribute('aPart', new BufferAttribute(a, 4));
    n.setAttribute('aPivot', new BufferAttribute(pv, 3));
    parts.push(n);
  };
  const none = () => 0;
  const neck: [number, number, number] = [0, 0.38, 0.02];
  // Body and haunches; chest is white on bicolour cats.
  tag(place(new SphereGeometry(1, 8, 6), [0, 0.21, 0], [0.12, 0, 0], [0.165, 0.215, 0.15]), PART.body, [0, 0, 0], (p) => (p.z > 0.06 && p.y < 0.33 ? 1 : 0));
  tag(place(new SphereGeometry(1, 7, 4), [0, 0.085, -0.02], [0, 0, 0], [0.175, 0.1, 0.17]), PART.body, [0, 0, 0], (p) => (p.z > 0.1 ? 1 : 0));
  // Head with a muzzle and ears.
  tag(place(new SphereGeometry(1, 8, 6), [0, 0.465, 0.03], [0, 0, 0], [0.118, 0.103, 0.105]), PART.head, neck, (p) => (p.y < 0.45 && p.z > 0.08 ? 1 : 0));
  tag(place(new SphereGeometry(1, 5, 3), [0, 0.432, 0.118], [0, 0, 0], [0.056, 0.038, 0.04]), PART.head, neck, () => 1);
  for (const sx of [-1, 1]) {
    tag(place(new ConeGeometry(0.048, 0.095, 3, 1, true), [sx * 0.066, 0.565, 0.02], [0.15, 0, -sx * 0.32]), PART.head, neck, none);
    tag(place(new SphereGeometry(0.021, 5, 3), [sx * 0.045, 0.483, 0.116], [0, 0, 0], [1, 0.82, 0.7]), PART.eye, neck, none);
  }
  // Front legs (raised to cheer), pivoting at the shoulders.
  for (const sx of [-1, 1]) {
    const leg = place(new CylinderGeometry(0.036, 0.03, 0.24, 5, 1, true), [sx * 0.075, 0.2, 0.1], [0.12, 0, 0]);
    const paw = place(new SphereGeometry(0.038, 5, 3), [sx * 0.075, 0.085, 0.115], [0, 0, 0], [1, 0.7, 1.2]);
    tag(merge([leg, paw]), sx < 0 ? PART.armL : PART.armR, [sx * 0.075, 0.31, 0.08], (p) => (p.y < 0.13 ? 0.6 : 0));
  }
  // Tail curled round the side, swishing from its base.
  const tail: BufferGeometry[] = [];
  let px = 0, pz = -0.15, a = -0.4;
  for (let k = 0; k < 3; k++) {
    const len = 0.11;
    const nx = px + Math.sin(a) * len * -1, nz = pz + Math.cos(a) * len * -1;
    tail.push(place(new CylinderGeometry(0.026 - k * 0.004, 0.028 - k * 0.004, len + 0.02, 4, 1, true), [(px + nx) / 2, 0.035, (pz + nz) / 2], [Math.PI / 2, a, 0]));
    px = nx; pz = nz; a += 0.75;
  }
  tag(merge(tail), PART.tail, [0, 0.04, -0.15], (_p) => 0);
  return mergeTagged(parts);
}

function mergeTagged(parts: BufferGeometry[]): BufferGeometry {
  // mergeGeometries keeps custom attributes when every part has them.
  const out = new BufferGeometry();
  const names = ['position', 'normal', 'uv', 'color', 'aPart', 'aPivot'] as const;
  for (const name of names) {
    const first = parts[0]!.getAttribute(name);
    const total = parts.reduce((n, p) => n + p.getAttribute(name).count, 0);
    const arr = new Float32Array(total * first.itemSize);
    let o = 0;
    for (const p of parts) { const src = p.getAttribute(name).array as Float32Array; arr.set(src, o); o += src.length; }
    out.setAttribute(name, new BufferAttribute(arr, first.itemSize));
  }
  for (const p of parts) p.dispose();
  out.computeBoundingSphere();
  return out;
}

const COATS = ['#c4722c', '#e0c08a', '#191616', '#ebe6dc', '#77716c', '#6c4b31', '#aaa49b', '#3a2a22', '#b9976f', '#d9873a', '#2b2624', '#f2efe8']
  .map((c) => new Color(c).convertSRGBToLinear());

const vertexHead = /* glsl */ `
attribute vec4 aPart;
attribute vec3 aPivot;
attribute vec3 aCoat;
attribute vec4 aMisc; // bicolour, phase, temperament, eye hue
uniform float uTime;
uniform float uPackX;
uniform float uExcite;
uniform float uFinishX;
uniform float uFinishCheer;
varying vec3 vCrowdColor;
varying float vEye;
mat3 rotX(float a) { float c = cos(a), s = sin(a); return mat3(1.0, 0.0, 0.0, 0.0, c, s, 0.0, -s, c); }
mat3 rotY(float a) { float c = cos(a), s = sin(a); return mat3(c, 0.0, -s, 0.0, 1.0, 0.0, s, 0.0, c); }
mat3 rotZ(float a) { float c = cos(a), s = sin(a); return mat3(c, s, 0.0, -s, c, 0.0, 0.0, 0.0, 1.0); }
`;

const vertexAnim = /* glsl */ `
  float part = aPart.x;
  vec3 seat = (modelMatrix * instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0)).xyz;
  float ph = aMisc.y * 6.2831;
  float temper = aMisc.z;
  // How excited this spectator is: the pack passing close by, or the finish.
  float near = exp(-pow((seat.x - uPackX) / 38.0, 2.0));
  float fin = uFinishCheer * exp(-pow((seat.x - uFinishX) / 70.0, 2.0));
  float e = clamp(uExcite * (0.25 + 0.75 * near) * (0.6 + 0.8 * temper) + fin * (0.5 + 0.7 * temper), 0.0, 1.0);
  // Occasional individual outbursts so the crowd is never perfectly still or in sync.
  float burst = smoothstep(0.82, 0.97, sin(uTime * (0.21 + temper * 0.17) + ph * 3.0));
  e = max(e, burst * 0.7);
  float hop = max(0.0, sin(uTime * (7.0 + temper * 3.0) + ph)) * 0.09 * smoothstep(0.55, 0.9, e);
  // Heads follow the pack, each with its own lag and nod.
  float dx = uPackX - seat.x;
  float yaw = clamp(atan(dx, 10.0 + 6.0 * temper), -1.1, 1.1) + sin(uTime * 0.6 + ph) * 0.12;
  float nod = sin(uTime * 1.3 + ph * 2.0) * 0.06 - 0.08;
  mat3 R = mat3(1.0);
  if (part > 0.5 && part < 2.5) R = rotY(yaw) * rotX(nod);
  else if (part > 2.5 && part < 4.5) {
    float side = part < 3.5 ? -1.0 : 1.0;
    float wave = sin(uTime * (9.0 + temper * 4.0) + ph + side) * 0.35;
    float raise = smoothstep(0.35, 0.85, e) * (side < 0.0 ? 1.0 : (temper > 0.5 ? 1.0 : 0.4));
    R = rotZ(side * raise * (0.4 + wave * 0.6)) * rotX(-raise * 2.5);
  } else if (part > 4.5) {
    R = rotY(sin(uTime * (1.4 + e * 4.0) + ph) * (0.35 + e * 0.4));
  }
  float breath = 1.0 + sin(uTime * 2.2 + ph) * 0.015;
`;

export class Crowd {
  readonly group = new Group();
  private mesh: InstancedMesh | null = null;
  private geo = spectatorGeometry();
  private material: MeshStandardMaterial;
  private uniforms = {
    uTime: { value: 0 }, uPackX: { value: 0 }, uExcite: { value: 0.3 },
    uFinishX: { value: 600 }, uFinishCheer: { value: 0 },
  };
  private modules = 0;
  private windowStart = -1;
  private seats: { slot: number; row: number; seat: number; jitter: number; scale: number; yaw: number }[] = [];
  private finishCheer = 0;

  constructor(private modulesX: number[], count: number, private onWindow: (from: number, to: number) => void) {
    this.group.name = 'crowd';
    const m = new MeshStandardMaterial({ roughness: 0.88, metalness: 0 });
    m.onBeforeCompile = (shader) => {
      Object.assign(shader.uniforms, this.uniforms);
      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', `#include <common>\n${vertexHead}`)
        .replace('#include <beginnormal_vertex>', `${vertexAnim}\nvec3 objectNormal = R * normal;\n#ifdef USE_TANGENT\nvec3 objectTangent = vec3(tangent.xyz);\n#endif`)
        .replace('#include <begin_vertex>', `vec3 transformed = (R * (position - aPivot) + aPivot) * breath;\ntransformed.y += hop;\n
          vEye = part > 1.5 && part < 2.5 ? 1.0 : 0.0;
          vec3 white = vec3(0.86, 0.84, 0.8);
          vCrowdColor = mix(aCoat, white, aPart.y * aMisc.x);
          vec3 eyeCol = mix(vec3(0.55, 0.62, 0.12), vec3(0.75, 0.5, 0.08), aMisc.w);
          vCrowdColor = mix(vCrowdColor, eyeCol, vEye);`);
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <common>', '#include <common>\nvarying vec3 vCrowdColor;\nvarying float vEye;')
        .replace('#include <color_fragment>', '#include <color_fragment>\ndiffuseColor.rgb = vCrowdColor;')
        .replace('#include <roughnessmap_fragment>', '#include <roughnessmap_fragment>\nroughnessFactor = mix(roughnessFactor, 0.15, vEye);')
        .replace('#include <emissivemap_fragment>', '#include <emissivemap_fragment>\ntotalEmissiveRadiance += vCrowdColor * vEye * 0.6;');
    };
    m.customProgramCacheKey = () => 'crowd-cat';
    this.material = m;
    this.setCount(count);
  }

  /** Rebuilds the instanced crowd for a new tier's spectator count. */
  setCount(count: number): void {
    if (this.mesh) { this.group.remove(this.mesh); this.mesh.dispose(); this.mesh = null; }
    this.modules = Math.min(crowdModules(count), this.modulesX.length);
    this.windowStart = -1;
    if (count <= 0) { this.onWindow(0, 0); return; }
    const r = rng(1234);
    // Pick occupied seats in a fixed pattern for one module, then repeat it for every module slot.
    this.seats = [];
    for (let slot = 0; slot < this.modules; slot++) for (let row = 0; row < STAND.rows; row++) for (let seat = 0; seat < SEATS_PER_ROW; seat++) {
      if (r() > OCCUPANCY) continue;
      this.seats.push({ slot, row, seat, jitter: (r() - 0.5) * 0.18, scale: 0.85 + r() * 0.35, yaw: (r() - 0.5) * 0.5 });
    }
    this.seats.length = Math.min(this.seats.length, count);
    const n = this.seats.length;
    const coat = new Float32Array(n * 3), misc = new Float32Array(n * 4);
    for (let i = 0; i < n; i++) {
      const c = COATS[Math.floor(r() * COATS.length)]!;
      const v = 0.85 + r() * 0.3;
      coat[i * 3] = c.r * v; coat[i * 3 + 1] = c.g * v; coat[i * 3 + 2] = c.b * v;
      misc[i * 4] = r() < 0.45 ? 1 : 0; misc[i * 4 + 1] = r(); misc[i * 4 + 2] = r(); misc[i * 4 + 3] = r();
    }
    const geo = this.geo.clone();
    geo.setAttribute('aCoat', new InstancedBufferAttribute(coat, 3));
    geo.setAttribute('aMisc', new InstancedBufferAttribute(misc, 4));
    const mesh = new InstancedMesh(geo, this.material, n);
    mesh.castShadow = false; mesh.receiveShadow = true;
    mesh.frustumCulled = false;
    mesh.name = 'crowd-cats';
    this.mesh = mesh;
    this.group.add(mesh);
  }

  private place(start: number): void {
    if (!this.mesh) return;
    const m = new Matrix4(), q = new Quaternion(), s = new Vector3(), p = new Vector3(), up = new Vector3(0, 1, 0);
    this.seats.forEach((st, i) => {
      const x0 = this.modulesX[start + st.slot]!;
      const seat = rowSeat(st.row);
      p.set(x0 + (st.seat + 0.5) * SEAT_SPACING + st.jitter, seat.y, seat.z + 0.08);
      q.setFromAxisAngle(up, st.yaw);
      this.mesh!.setMatrixAt(i, m.compose(p, q, s.setScalar(st.scale)));
    });
    this.mesh.instanceMatrix.needsUpdate = true;
  }

  update(dt: number, timeS: number, focusX: number, packX: number, phase: Phase, phaseElapsedMs: number): void {
    if (this.modules > 0) {
      const start = crowdWindowStart(focusX, this.modules, this.modulesX.length);
      if (start !== this.windowStart) {
        this.windowStart = start;
        this.place(start);
        this.onWindow(start, start + this.modules);
      }
    }
    const u = this.uniforms;
    u.uTime.value = timeS;
    u.uPackX.value += (packX - u.uPackX.value) * (1 - Math.exp(-dt * 3));
    const target = phase === 'running' ? 0.75 : phase === 'finished' ? 0.45 : 0.25;
    u.uExcite.value += (target - u.uExcite.value) * (1 - Math.exp(-dt * 1.5));
    // Big cheer at the finish for the first seconds of results, then settling.
    const cheer = phase === 'finished' ? Math.max(0, 1 - phaseElapsedMs / 14000) : 0;
    this.finishCheer += (cheer - this.finishCheer) * (1 - Math.exp(-dt * 3));
    u.uFinishCheer.value = this.finishCheer;
  }

  /** The finish cheer can also be raised directly when a cat crosses the line. */
  cheer(amount = 1): void { this.finishCheer = Math.max(this.finishCheer, amount); }
}
